// @ts-strict-ignore
interface Equality<T> {
  equals(other: T): boolean;
}

export function definedValues<T>(value: T): boolean {
  return value !== null && value !== undefined;
}

export function toString(value: Object): string {
  return value.toString();
}

export function unique<T>(value: T, index: number, array: T[]): boolean {
  return array.indexOf(value) === index;
}

export function uniqueBy<T, K>(keyFunc: (item: T) => K): (value: T, index: number, array: T[]) => boolean {
  return (value: T, index: number, array: T[]): boolean => {
    const key = keyFunc(value);
    return array.findIndex(item => keyFunc(item) === key) === index;
  };
}

export function numeric(s1: string, s2: string): number {
  return s1.localeCompare(s2, undefined, {numeric: true});
}

export function numericBy<T>(keyFunc: (item: T) => string): (a: T, b: T) => number {
  return (a: T, b: T) => numeric(keyFunc(a), keyFunc(b));
}

export function allValuesSet<T>(array: (T | undefined | null)[]): array is T[] {
  return array.every(definedValues);
}

export class ArrayUtils {
  public static isEmpty(a: ArrayLike<any>): boolean {
    return !a || a.length === 0;
  }

  public static isNotEmpty(a: ArrayLike<any>): boolean {
    return !this.isEmpty(a);
  }

  public static addAll(target: any[], source: any[]): void {
    if (!target || this.isEmpty(source)) {
      return;
    }
    source.forEach((element: any) => target.push(element));
  }

  public static mergeArrays(target: any[], source: any[]): void {
    if (!target || this.isEmpty(source)) {
      return;
    }
    for (let i = 0; i < source.length; i++) {
      const item: any = source[i];
      if (target.indexOf(item) === -1) {
        target.push(item);
      }
    }
  }

  public static fillArray<T>(a: T[], startIndex: number, count: number, val: T = null): void {
    if (a === null || startIndex < 0 || count <= 0) {
      return;
    }
    if (startIndex >= a.length) {
      return;
    }
    const maxLength = Math.min(startIndex + count, a.length);
    for (let i = startIndex; i < maxLength; i++) {
      a[i] = val;
    }
  }

  public static notInArray = function (item: any, index: number, array: any[]) {
    return this.indexOf(item) === -1;
  };

  public static bothArraysContainTheSameElements(a: any[], b: any[]): boolean {
    if (!a) {
      return b === null;
    }
    if (!b) {
      return false;
    }
    let result: boolean = this.sourceArrayContainsAllElementsFromTargetArray(a, b);
    if (result) {
      result = this.sourceArrayContainsAllElementsFromTargetArray(b, a);
    }
    return result;
  }

  public static sourceArrayContainsAllElementsFromTargetArray(source: any[], target: any[]): boolean {
    if (!source || !target) {
      return false;
    }
    for (const value of target) {
      if (source.indexOf(value) === -1) {
        return false;
      }
    }
    return true;
  }

  public static removeItem<T>(item: T, source: T[]): boolean {
    if (!source) {
      return false;
    }
    const itemIndex: number = this.getItemIndex(item, source);
    if (itemIndex === -1) {
      return false;
    }
    source.splice(itemIndex, 1);
    return true;
  }

  public static getItemIndex(item: any, source: any[]): number {
    if (!source) {
      return -1;
    }
    return source.indexOf(item);
  }

  public static sourceArrayContainsElementFromTargetArray(source: any[], target: any[]): boolean {
    if (!source || source.length === 0 || !target || target.length === 0) {
      return false;
    }
    for (const targetElement of target) {
      if (source.indexOf(targetElement) !== -1) {
        return true;
      }
    }
    return false;
  }

  /** In-place operation that removes all items from the target array that appear in the source array
   * @returns whether or not any items were removed */
  public static removeAllItemsInArray(target: any[], source: any[]): boolean {
    if (!source || source.length === 0) {
      return false;
    }
    let targetChanged: boolean = false;
    for (let i: number = 0; i < source.length; i++) {
      const item: any = source[i];
      const itemIndex: number = target.indexOf(item);
      if (itemIndex !== -1) {
        target.splice(itemIndex, 1);
        targetChanged = true;
      }
    }
    return targetChanged;
  }

  public static createIntSequenceArray(startVal: number, endVal: number): number[] {
    const result: number[] = new Array(endVal - startVal + 1);
    for (let i = 0; i < result.length; i++) {
      result[i] = startVal + i;
    }
    return result;
  }

  public static numberArrayFromString(input: string, separator: string = ','): number[] {
    return input.split(separator).map(Number);
  }

  public static intArrayFromString(input: string, separator: string = ',') {
    return input.split(separator).map((stringValue) => Math.floor(Number(stringValue)));
  }

  public static stringArrayFromString(input: string, separator: string = ',') {
    return input.split(separator).map((stringValue) => stringValue.trim());
  }

  /** @returns Whether or not two arrays are equal. If the arrays contain objects, these must have an equals() method for this to work */
  public static equals(array1: any[], array2: any[]): boolean {
    if (array1 === array2) {
      return true;
    }
    if (!array1 || !array2) {
      return false;
    }
    const length = array1.length;
    if (array2.length !== length) {
      return false;
    }
    for (let i: number = 0; i < length; i++) {
      const object1 = array1[i];
      const object2 = array2[i];
      if (!object1) {
        return object2 === null || object2 === undefined;
      }
      if (!(typeof object1['equals'] === 'function') || !(typeof object2['equals'] === 'function')) {
        return false;
      }
      if (!object1.equals(object2)) {
        return false;
      }
    }
    return true;
  }

  /** Removes all elements from the source array that evaluate to true with the given condition-function */
  public static removeFromArray<T>(source: T[], condition: (element: T) => boolean): void {
    if (this.isEmpty(source) || !condition) {
      return;
    }
    let i: number = source.length;
    while (i--) {
      const element: any = source[i];
      if (condition(element)) {
        source.splice(i, 1);
      }
    }
  }

  /** @returns a new array in which only the first occurrence of each distinct element of the source array is included */
  public static distinctByEquality<T extends Equality<T>>(array: T[]): T[] {
    return array.filter((value, index, array) => array.findIndex(item => item.equals(value)) === index);
  }

  public static equalsByEquality<T extends Equality<T>>(array1: T[], array2: T[]): boolean {
    if(array1.length !== array2.length) {
      return false;
    }

    for (let i = 0; i < array1.length; i++) {
      if(!array1[i].equals(array2[i])) {
        return false;
      }
    }

    return true;
  }

  /** @returns a new array in which only the first occurrence of each distinct element of the source array is included */
  public static distinct<T>(array: T[]): T[] {
    return [...new Set(array)];
  }

  public static range(start: number, end: number): number[] {
    return Array.from({length: end - start + 1}, (_, i) => i + start);
  }

  public static toMap<T, K>(array: T[], keyFunction: (T) => K): Map<K, T> {
    return array.reduce((map, item) => {
      map.set(keyFunction(item), item);
      return map;
    }, new Map<K, T>());
  }

  public static indexOf(array: any[], item: any): number {
    for (let i = 0; i < array.length; i++) {
      if (typeof array[i] === typeof item && typeof item['equals'] === 'function') {
        if (item.equals(array[i])) {
          return i;
        }
      } else if (item === array[i]) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Checks if the subset is contained in the superset without gaps.
   *
   * superset = [1,2,3,4,5,6], subset = [3,4,5] => true
   * superset = [1,2,3,4,5,6], subset = [3,5,6] => false
   * @returns true if the subset is contained without gaps in the superset.
   **/
  public static includesContiguous(superset: any[], subset: any[]): boolean {
    const supersetLength = superset.length;
    const subsetLength = subset.length;

    if (subsetLength === 0) return true;

    for (let i = 0; i <= supersetLength - subsetLength; i++) {
      let match = true;
      for (let j = 0; j < subsetLength; j++) {
        if (superset[i + j] !== subset[j]) {
          match = false;
          break;
        }
      }
      if (match) return true;
    }
    return false;
  }

  /**
   * Groups consecutive array elements into groups based on a key function.
   * Unlike standard grouping, this maintains the original order and can have multiple groups with the same key if elements are not consecutive.
   * @param array The source array to group
   * @param keyFunction A function that returns the grouping key for each element
   * @returns An array of groups, where each group has a groupId and an array of items belonging to that group
   * @example
   * // Groups [1,1,2,1] into [{groupId:1, items:[1,1]}, {groupId:2, items:[2]}, {groupId:1, items:[1]}]
   */
  public static groupByPreserveOrder<T, K>(array: T[], keyFunction: (item: T) => K): { groupId: K, items: T[] }[] {
    return array.reduce((groups: { groupId: K, items: T[] }[], item: T) => {
      const key = keyFunction(item);
      const previousGroup = groups.at(-1);
      if (previousGroup?.groupId === key) {
        previousGroup.items.push(item);
      } else {
        groups.push({groupId: key, items: [item]});
      }
      return groups;
    }, []);
  }

  /** Splits an array into two arrays. The first one containing the items that matched the isValid check, the second one the rest.
   * @returns [[...conditionSucceeded], [...conditionFailed]] */
  public static splitByCondition<T>(array: T[], condition: (v: T) => boolean): [T[], T[]] {
    return array.reduce(([pass, fail], elem) => {
      return condition(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]];
    }, [[], []]);
  }

  public static moveItem<T>(array: T[], fromIndex: number, toIndex: number): T[] {
    if (fromIndex < 0 || fromIndex >= array.length || toIndex < 0 || toIndex >= array.length) {
      throw new Error('Invalid indices for moving item in array');
    }
    const [ item ] = array.splice(fromIndex, 1);
    array.splice(toIndex, 0, item);
    return array;
  }

  public static transpose<T>(array: T[][]): T[][] {
    if (this.isEmpty(array) || this.isEmpty(array[0])) {
      return [];
    }
    return array[0].map((_, colIndex) => array.map(row => row[colIndex]));
  }

  public static unique<T>(array: T[]): T[] {
    return array.filter((value, index, array) => unique(value, index, array));
  }

  public static definedValues<T>(array: T[]): T[] {
    return array.filter((value) => definedValues(value));
  }
}
